import React, { useState, useMemo } from 'react';
import { View, Text, TouchableOpacity, Modal, ScrollView, TextInput, StyleSheet, Image, Platform } from 'react-native';
import { MdArrowDropDown, MdClose, MdSearch } from 'react-icons/md';

// Data ported from archive/js/travel-create.js
const COUNTRIES = [
  { code: 'CN', name: 'China', flag: '🇨🇳', flagUrl: 'https://flagcdn.com/cn.svg', cities: ['Beijing', 'Shanghai', 'Guangzhou', 'Shenzhen', 'Hangzhou', 'Chengdu'] },
  { code: 'HK', name: 'Hong Kong', flag: '🇭🇰', flagUrl: 'https://flagcdn.com/hk.svg', cities: ['Hong Kong'] },
  { code: 'MO', name: 'Macau', flag: '🇲🇴', flagUrl: 'https://flagcdn.com/mo.svg', cities: ['Macau'] },
  { code: 'TW', name: 'Taiwan', flag: '🇹🇼', flagUrl: 'https://flagcdn.com/tw.svg', cities: ['Taipei', 'Kaohsiung', 'Taichung', 'Tainan'] },
  { code: 'JP', name: 'Japan', flag: '🇯🇵', flagUrl: 'https://flagcdn.com/jp.svg', cities: ['Tokyo', 'Osaka', 'Kyoto', 'Nagoya', 'Fukuoka', 'Sapporo'] },
  { code: 'KR', name: 'South Korea', flag: '🇰🇷', flagUrl: 'https://flagcdn.com/kr.svg', cities: ['Seoul', 'Busan', 'Daegu', 'Incheon'] },
  { code: 'TH', name: 'Thailand', flag: '🇹🇭', flagUrl: 'https://flagcdn.com/th.svg', cities: ['Bangkok', 'Chiang Mai', 'Phuket', 'Pattaya'] },
  { code: 'SG', name: 'Singapore', flag: '🇸🇬', flagUrl: 'https://flagcdn.com/sg.svg', cities: ['Singapore'] },
  { code: 'MY', name: 'Malaysia', flag: '🇲🇾', flagUrl: 'https://flagcdn.com/my.svg', cities: ['Kuala Lumpur', 'Penang', 'Malacca', 'Johor Bahru'] },
  { code: 'VN', name: 'Vietnam', flag: '🇻🇳', flagUrl: 'https://flagcdn.com/vn.svg', cities: ['Ho Chi Minh City', 'Hanoi', 'Da Nang', 'Nha Trang'] },
  { code: 'ID', name: 'Indonesia', flag: '🇮🇩', flagUrl: 'https://flagcdn.com/id.svg', cities: ['Jakarta', 'Bali', 'Yogyakarta', 'Surabaya'] },
  { code: 'IN', name: 'India', flag: '🇮🇳', flagUrl: 'https://flagcdn.com/in.svg', cities: ['New Delhi', 'Mumbai', 'Bangalore', 'Kolkata'] },
  { code: 'AE', name: 'United Arab Emirates', flag: '🇦🇪', flagUrl: 'https://flagcdn.com/ae.svg', cities: ['Dubai', 'Abu Dhabi'] },
  { code: 'GB', name: 'United Kingdom', flag: '🇬🇧', flagUrl: 'https://flagcdn.com/gb.svg', cities: ['London', 'Edinburgh', 'Manchester', 'Liverpool'] },
  { code: 'FR', name: 'France', flag: '🇫🇷', flagUrl: 'https://flagcdn.com/fr.svg', cities: ['Paris', 'Lyon', 'Marseille', 'Nice'] },
  { code: 'DE', name: 'Germany', flag: '🇩🇪', flagUrl: 'https://flagcdn.com/de.svg', cities: ['Berlin', 'Munich', 'Hamburg', 'Frankfurt'] },
  { code: 'IT', name: 'Italy', flag: '🇮🇹', flagUrl: 'https://flagcdn.com/it.svg', cities: ['Rome', 'Milan', 'Venice', 'Florence'] },
  { code: 'ES', name: 'Spain', flag: '🇪🇸', flagUrl: 'https://flagcdn.com/es.svg', cities: ['Madrid', 'Barcelona', 'Valencia', 'Seville'] },
  { code: 'NL', name: 'Netherlands', flag: '🇳🇱', flagUrl: 'https://flagcdn.com/nl.svg', cities: ['Amsterdam', 'Rotterdam', 'The Hague'] },
  { code: 'CH', name: 'Switzerland', flag: '🇨🇭', flagUrl: 'https://flagcdn.com/ch.svg', cities: ['Zurich', 'Geneva', 'Basel'] },
  { code: 'US', name: 'United States', flag: '🇺🇸', flagUrl: 'https://flagcdn.com/us.svg', cities: ['New York', 'Los Angeles', 'Chicago', 'San Francisco', 'Las Vegas', 'Miami'] },
  { code: 'CA', name: 'Canada', flag: '🇨🇦', flagUrl: 'https://flagcdn.com/ca.svg', cities: ['Toronto', 'Vancouver', 'Montreal', 'Ottawa'] },
  { code: 'AU', name: 'Australia', flag: '🇦🇺', flagUrl: 'https://flagcdn.com/au.svg', cities: ['Sydney', 'Melbourne', 'Brisbane', 'Perth'] },
  { code: 'NZ', name: 'New Zealand', flag: '🇳🇿', flagUrl: 'https://flagcdn.com/nz.svg', cities: ['Auckland', 'Wellington', 'Christchurch'] }
];

interface CountryCitySelectorProps {
  value?: string;
  onChange: (value: string) => void;
}

const CountryCitySelector: React.FC<CountryCitySelectorProps> = ({ value, onChange }) => {
  const [modalVisible, setModalVisible] = useState(false);
  const [step, setStep] = useState<'country' | 'city'>('country');
  const [selectedCountry, setSelectedCountry] = useState<typeof COUNTRIES[0] | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Parse initial value to try and set selected country
  React.useEffect(() => {
    if (value && !selectedCountry) {
      // Try to find country in value (e.g. "Japan, Tokyo" or "JP-Tokyo")
      const found = COUNTRIES.find(c => value.includes(c.name) || value.includes(c.code));
      if (found) {
        setSelectedCountry(found);
      }
    }
  }, [value]);

  const filteredCountries = useMemo(() => {
    if (!searchQuery) return COUNTRIES;
    const q = searchQuery.toLowerCase();
    return COUNTRIES.filter(c => c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q));
  }, [searchQuery]);

  const handleCountrySelect = (country: typeof COUNTRIES[0]) => {
    setSelectedCountry(country);
    setStep('city');
    setSearchQuery('');
  };

  const handleCitySelect = (city: string) => {
    if (selectedCountry) {
      onChange(`${selectedCountry.name}, ${city}`);
      setModalVisible(false);
      setStep('country');
      setSearchQuery('');
    }
  };

  const renderTrigger = () => {
    let display = 'Select Destination';
    let flagUrl = '';
    
    if (value) {
      // Try to parse "Country, City" or "Code-City" or just show value
      let countryCode = '';
      let city = '';
      
      // Try comma separator first (Legacy format: "Japan, Tokyo")
      if (value.includes(',')) {
        const parts = value.split(',');
        const countryName = parts[0].trim();
        city = parts.slice(1).join(',').trim();
        const country = COUNTRIES.find(c => c.name === countryName || c.code === countryName);
        if (country) {
          countryCode = country.code;
          flagUrl = country.flagUrl;
          display = `${country.name} · ${city}`;
        } else {
          display = value;
        }
      } 
      // Try hyphen separator (Code format: "JP-Tokyo")
      else if (value.includes('-')) {
        const parts = value.split('-');
        const codeOrName = parts[0].trim();
        city = parts.slice(1).join('-').trim();
        const country = COUNTRIES.find(c => c.code === codeOrName || c.name === codeOrName);
        if (country) {
          countryCode = country.code;
          flagUrl = country.flagUrl;
          display = `${country.name} · ${city}`;
        } else {
          display = value;
        }
      }
      // Fallback: try to find country in the string
      else {
        const country = COUNTRIES.find(c => value.includes(c.name));
        if (country) {
          flagUrl = country.flagUrl;
          display = value;
        } else {
          display = value;
        }
      }
    }

    return (
      <TouchableOpacity 
        style={styles.trigger} 
        onPress={() => {
          setModalVisible(true);
          setStep('country');
          setSearchQuery('');
        }}
      >
        <View style={styles.triggerContent}>
          {flagUrl ? (
            <Image source={{ uri: flagUrl }} style={styles.flagIcon} />
          ) : (
            <Text style={styles.placeholderIcon}>🌏</Text>
          )}
          <Text style={[styles.triggerText, !value && styles.placeholderText]}>
            {display}
          </Text>
        </View>
        <MdArrowDropDown size={24} color="#666" />
      </TouchableOpacity>
    );
  };

  return (
    <>
      {renderTrigger()}

      <Modal
        visible={modalVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setModalVisible(false)}
      >
        <View style={styles.overlay}>
          <View style={styles.modalContainer}>
            <View style={styles.header}>
              <Text style={styles.headerTitle}>
                {step === 'country' ? 'Select Country' : `Select City in ${selectedCountry?.name}`}
              </Text>
              <TouchableOpacity onPress={() => setModalVisible(false)} style={styles.closeButton}>
                <MdClose size={24} color="#666" />
              </TouchableOpacity>
            </View>

            <View style={styles.searchContainer}>
              <MdSearch size={20} color="#999" style={styles.searchIcon} />
              <TextInput
                style={styles.searchInput}
                placeholder={step === 'country' ? "Search country..." : "Search city..."}
                value={searchQuery}
                onChangeText={setSearchQuery}
                autoFocus={false}
              />
            </View>

            <ScrollView style={styles.list} keyboardShouldPersistTaps="handled">
              {step === 'country' ? (
                filteredCountries.map(country => (
                  <TouchableOpacity
                    key={country.code}
                    style={styles.item}
                    onPress={() => handleCountrySelect(country)}
                  >
                    <Image source={{ uri: country.flagUrl }} style={styles.itemFlag} />
                    <Text style={styles.itemText}>{country.name}</Text>
                    <Text style={styles.itemCode}>{country.code}</Text>
                  </TouchableOpacity>
                ))
              ) : (
                <>
                  {selectedCountry?.cities
                    .filter(city => city.toLowerCase().includes(searchQuery.toLowerCase()))
                    .map(city => (
                      <TouchableOpacity
                        key={city}
                        style={styles.item}
                        onPress={() => handleCitySelect(city)}
                      >
                        <Text style={styles.itemText}>{city}</Text>
                      </TouchableOpacity>
                    ))}
                  
                  <TouchableOpacity 
                    style={styles.backButton}
                    onPress={() => {
                      setStep('country');
                      setSearchQuery('');
                    }}
                  >
                    <Text style={styles.backButtonText}>← Back to Country Selection</Text>
                  </TouchableOpacity>
                </>
              )}
            </ScrollView>
          </View>
        </View>
      </Modal>
    </>
  );
};

const styles = StyleSheet.create({
  trigger: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    padding: 12,
    backgroundColor: '#fff',
  },
  triggerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  flagIcon: {
    width: 24,
    height: 16,
    borderRadius: 2,
    marginRight: 8,
    resizeMode: 'cover',
  },
  placeholderIcon: {
    fontSize: 16,
    marginRight: 8,
  },
  triggerText: {
    fontSize: 16,
    color: '#333',
  },
  placeholderText: {
    color: '#999',
  },
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContainer: {
    width: '100%',
    maxWidth: 400,
    backgroundColor: '#fff',
    borderRadius: 16,
    maxHeight: '80%',
    display: 'flex',
    flexDirection: 'column',
    ...Platform.select({
      web: {
        boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
      },
      default: {
        elevation: 5,
      },
    }),
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#111',
  },
  closeButton: {
    padding: 4,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  searchIcon: {
    marginRight: 8,
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    padding: 4,
    outlineStyle: 'none',
  } as any,
  list: {
    flex: 1,
  },
  item: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#f5f5f5',
  },
  itemFlag: {
    width: 28,
    height: 20,
    borderRadius: 3,
    marginRight: 12,
    resizeMode: 'cover',
    backgroundColor: '#eee',
  },
  itemText: {
    fontSize: 16,
    color: '#333',
    flex: 1,
  },
  itemCode: {
    fontSize: 14,
    color: '#999',
    marginLeft: 8,
  },
  backButton: {
    padding: 16,
    alignItems: 'center',
  },
  backButtonText: {
    color: '#666',
    fontSize: 14,
  },
});

export default CountryCitySelector;
